import { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, ScrollText } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Skeleton } from "@/components/ui/skeleton";

const TermsOfService = () => {
  const [title, setTitle] = useState("Terms of Use");
  const [body, setBody] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("site_content")
      .select("*")
      .eq("content_key", "terms_of_use")
      .single()
      .then(({ data }) => {
        if (data) {
          setTitle(data.title);
          setBody(data.body);
          const stamp = (data as { updated_at?: string }).updated_at;
          if (stamp) setUpdatedAt(stamp);
        }
        setIsLoading(false);
      });
  }, []);

  return (
    <div className="bg-background">
      <header className="border-b border-border bg-card/50">
        <div className="container mx-auto max-w-3xl px-4 pb-10 pt-10 md:pb-14 md:pt-14">
          <Link
            to="/"
            className="inline-flex min-h-11 items-center gap-2 font-body text-sm text-foreground/80 transition-colors hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Wine Trail
          </Link>
          <div className="mt-4 flex items-start gap-4">
            <span className="hidden h-12 w-12 shrink-0 items-center justify-center border border-primary/50 bg-primary/10 text-primary sm:flex">
              <ScrollText className="h-6 w-6" />
            </span>
            <div>
              <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-accent">
                Legal
              </p>
              <h1 className="mt-2 font-display text-3xl font-bold uppercase leading-tight tracking-wide text-foreground md:text-5xl">
                {title}
              </h1>
              <p className="mt-3 min-h-5 font-body text-sm text-foreground/70">
                {updatedAt && (
                  <>
                    Last updated{" "}
                    {new Date(updatedAt).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-3xl px-4 py-10 md:py-14">
        {isLoading ? (
          <div className="min-h-screen space-y-4">
            <Skeleton className="h-7 w-1/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="mt-8 h-7 w-2/5" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-10/12" />
          </div>
        ) : (
          <article className="border border-border bg-card p-6 md:p-10">
            <ReactMarkdown
              components={{
                h2: ({ children }) => (
                  <h2 className="mt-10 mb-4 border-l-2 border-accent pl-4 font-display text-xl font-bold uppercase tracking-wide text-foreground first:mt-0 md:text-2xl">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="mt-8 mb-3 font-display text-lg font-semibold text-foreground">
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p className="mb-5 font-body text-base leading-7 text-foreground/85">
                    {children}
                  </p>
                ),
                li: ({ children }) => (
                  <li className="mb-2 font-body text-base leading-7 text-foreground/85 marker:text-accent">
                    {children}
                  </li>
                ),
                ul: ({ children }) => (
                  <ul className="mb-6 list-disc space-y-1 pl-5">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="mb-6 list-decimal space-y-1 pl-5">
                    {children}
                  </ol>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold text-foreground">
                    {children}
                  </strong>
                ),
                a: ({ href, children }) => (
                  <a
                    href={href}
                    className="text-primary underline underline-offset-2 hover:text-accent"
                  >
                    {children}
                  </a>
                ),
                hr: () => <hr className="my-8 border-border" />,
              }}
            >
              {body}
            </ReactMarkdown>
          </article>
        )}

        <p className="mt-8 font-body text-sm text-foreground/70">
          Questions about these terms?{" "}
          <Link
            to="/contact"
            className="text-primary underline underline-offset-2 hover:text-accent"
          >
            Contact us
          </Link>
          .
        </p>
      </main>
    </div>
  );
};

export default TermsOfService;
